import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { salesService, operadorasService, usersService } from '../services/api';
import { formatDate, formatDocumento } from '../utils/masks';
import '../styles/VendasPagamento.css';

const DIAS_SEMANA = [
  { value: 0, label: 'Domingo' },
  { value: 1, label: 'Segunda' },
  { value: 2, label: 'Terça' },
  { value: 3, label: 'Quarta' },
  { value: 4, label: 'Quinta' },
  { value: 5, label: 'Sexta' },
  { value: 6, label: 'Sábado' }
];

const LIMITE_POR_PAGINA = 20;

const VendasPagamento = () => {
  const [vendas, setVendas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState('');
  const [busca, setBusca] = useState('');
  const [buscaInput, setBuscaInput] = useState('');
  const [filtroOperadoraId, setFiltroOperadoraId] = useState('');
  const [filtroVendedorId, setFiltroVendedorId] = useState('');
  const [vendedores, setVendedores] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totais, setTotais] = useState({ aPagar: 0, pagas: 0, estornadas: 0, valorTotal: 0 });
  const [selectedVenda, setSelectedVenda] = useState(null);
  const [pagamentoData, setPagamentoData] = useState({
    status_pagamento: 'a_pagar',
    data_pagamento: ''
  });
  const [updating, setUpdating] = useState(false);
  const [operadoras, setOperadoras] = useState([]);
  const [operadoraResumo, setOperadoraResumo] = useState(null);
  const [loadingResumo, setLoadingResumo] = useState(false);
  const [savingFluxoId, setSavingFluxoId] = useState(null);

  useEffect(() => {
    loadVendasConcluidas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroStatus, page, busca, filtroOperadoraId, filtroVendedorId]);

  useEffect(() => {
    loadOperadoras();
  }, []);

  useEffect(() => {
    loadVendedores();
  }, []);

  const loadVendedores = async () => {
    try {
      const users = await usersService.getAll();
      const list = (users || []).filter(u =>
        u.role === 'vendedor' || u.role === 'admin' || u.role === 'diretor'
      );
      setVendedores(list);
    } catch (error) {
      console.error('Erro ao carregar vendedores:', error);
    }
  };

  const loadOperadoras = async () => {
    try {
      const data = await operadorasService.getAll();
      setOperadoras(data);
    } catch (error) {
      console.error('Erro ao carregar operadoras:', error);
    }
  };

  const loadVendasConcluidas = async () => {
    try {
      setLoading(true);
      const params = {
        status_pagamento: filtroStatus || undefined,
        page,
        limit: LIMITE_POR_PAGINA,
        busca: busca.trim() || undefined,
        operadora_id: filtroOperadoraId || undefined,
        vendedor_id: filtroVendedorId || undefined
      };
      const data = await salesService.getVendasConcluidas(params);
      const isPaginated = data && typeof data === 'object' && Array.isArray(data.items);
      if (isPaginated) {
        setVendas(data.items);
        setTotal(data.total ?? 0);
        setTotalPages(data.totalPages ?? 0);
        if (data.totais) setTotais(data.totais);
      } else {
        const list = Array.isArray(data) ? data : [];
        setVendas(list);
        setTotal(list.length);
        setTotalPages(1);
        setTotais({
          aPagar: list.filter(v => v.status_pagamento === 'a_pagar').length,
          pagas: list.filter(v => v.status_pagamento === 'pago').length,
          estornadas: list.filter(v => v.status_pagamento === 'estornado').length,
          valorTotal: list.filter(v => v.status_pagamento === 'pago').reduce((s, v) => s + parseFloat(v.valor_total || 0), 0)
        });
      }
    } catch (error) {
      console.error('Erro ao carregar vendas concluídas:', error);
      alert('Erro ao carregar vendas concluídas');
    } finally {
      setLoading(false);
    }
  };

  const handleBuscaSubmit = (e) => {
    e?.preventDefault();
    setBusca(buscaInput);
    setPage(1);
  };

  const handleFiltroStatusChange = (value) => {
    setFiltroStatus(value);
    setPage(1);
  };

  const handleFiltroOperadoraChange = (value) => {
    setFiltroOperadoraId(value);
    setPage(1);
  };

  const handleFiltroVendedorChange = (value) => {
    setFiltroVendedorId(value);
    setPage(1);
  };

  const getStatusPagamentoClass = (status) => {
    const classes = {
      'a_pagar': 'status-a-pagar',
      'pago': 'status-pago',
      'estornado': 'status-estornado'
    };
    return classes[status] || '';
  };

  const getStatusPagamentoText = (status) => {
    const texts = {
      'a_pagar': 'A Pagar',
      'pago': 'Pago',
      'estornado': 'Estornado'
    };
    return texts[status] || status;
  };

  const openPagamento = (venda) => {
    setSelectedVenda(venda);
    setPagamentoData({
      status_pagamento: venda.status_pagamento || 'a_pagar',
      data_pagamento: venda.data_pagamento || ''
    });
  };

  const closePagamento = () => {
    setSelectedVenda(null);
    setPagamentoData({
      status_pagamento: 'a_pagar',
      data_pagamento: ''
    });
  };

  const handlePagamentoChange = (e) => {
    const { name, value } = e.target;
    setPagamentoData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdatePagamento = async (e) => {
    e.preventDefault();

    if (!pagamentoData.status_pagamento) {
      alert('Selecione um status de pagamento');
      return;
    }

    // Se o status for 'pago' e não houver data, usar a data atual
    if (pagamentoData.status_pagamento === 'pago' && !pagamentoData.data_pagamento) {
      const today = new Date().toISOString().split('T')[0];
      pagamentoData.data_pagamento = today;
    }

    try {
      setUpdating(true);
      await salesService.updateStatusPagamento(
        selectedVenda.id,
        pagamentoData.status_pagamento,
        pagamentoData.status_pagamento === 'pago' ? pagamentoData.data_pagamento : null
      );
      
      alert('✅ Status de pagamento atualizado com sucesso!');
      closePagamento();
      loadVendasConcluidas();
    } catch (error) {
      console.error('Erro ao atualizar status de pagamento:', error);
      alert('❌ Erro ao atualizar status de pagamento: ' + (error.response?.data?.error || error.message));
    } finally {
      setUpdating(false);
    }
  };


  const handleFluxoChange = (operadoraId, field, value) => {
    setOperadoras(prev =>
      prev.map(op =>
        op.id === operadoraId
          ? {
              ...op,
              [field]: value === '' || value === null ? null : (field === 'fluxo_dia_semana' || field === 'fluxo_dia_mes' ? parseInt(value, 10) : value)
            }
          : op
      )
    );
  };

  const handleSaveFluxo = async (operadora) => {
    try {
      setSavingFluxoId(operadora.id);
      const payload = {
        fluxo_tipo: operadora.fluxo_tipo || null,
        fluxo_dia_semana: operadora.fluxo_tipo === 'semanal' ? (operadora.fluxo_dia_semana != null ? operadora.fluxo_dia_semana : 1) : null,
        fluxo_dia_mes: operadora.fluxo_tipo === 'mensal' ? (operadora.fluxo_dia_mes != null ? operadora.fluxo_dia_mes : 1) : null
      };
      await operadorasService.update(operadora.id, payload);
      await loadOperadoras();
      alert('Fluxo de pagamento salvo com sucesso.');
    } catch (error) {
      console.error('Erro ao salvar fluxo:', error);
      alert('Erro ao salvar fluxo: ' + (error.response?.data?.error || error.message));
    } finally {
      setSavingFluxoId(null);
    }
  };

  const openResumoModal = async (operadora) => {
    try {
      setLoadingResumo(true);
      setOperadoraResumo(null);
      const data = await salesService.getResumoPagamentoPorOperadora(operadora.id);
      setOperadoraResumo(data);
    } catch (error) {
      console.error('Erro ao carregar resumo:', error);
      alert('Erro ao carregar resumo: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoadingResumo(false);
    }
  };

  const closeResumoModal = () => {
    setOperadoraResumo(null);
  };

  return (
    <Layout>
      <div className="vendas-pagamento-page">
        <div className="page-header">
          <h1>💰 Controle de Pagamentos</h1>
          <p>Gerencie o status de pagamento das vendas concluídas</p>
        </div>

        {/* Cards de estatísticas */}
        <div className="stats-cards">
          <div className="stat-card">
            <div className="stat-label">A Pagar</div>
            <div className="stat-value status-a-pagar">{totais.aPagar}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Pagas</div>
            <div className="stat-value status-pago">{totais.pagas}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Estornadas</div>
            <div className="stat-value status-estornado">{totais.estornadas}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Valor Total Pago</div>
            <div className="stat-value">R$ {Number(totais.valorTotal ?? 0).toFixed(2).replace('.', ',')}</div>
          </div>
        </div>

        <div className="filters">
          <form className="filter-group filter-busca" onSubmit={handleBuscaSubmit}>
            <label>Buscar (CPF, CNPJ ou nome):</label>
            <input
              type="text"
              value={buscaInput}
              onChange={(e) => setBuscaInput(e.target.value)}
              placeholder="Digite CPF, CNPJ ou nome do cliente..."
              className="filter-input"
            />
            <button type="submit" className="btn-primary btn-sm">Buscar</button>
          </form>
          <div className="filter-group">
            <label>Vendedor:</label>
            <select
              value={filtroVendedorId}
              onChange={(e) => handleFiltroVendedorChange(e.target.value)}
              className="filter-select"
            >
              <option value="">Todos</option>
              {vendedores.map((v) => (
                <option key={v.id} value={v.id}>{v.nome || v.username}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Operadora:</label>
            <select
              value={filtroOperadoraId}
              onChange={(e) => handleFiltroOperadoraChange(e.target.value)}
              className="filter-select"
            >
              <option value="">Todas</option>
              {operadoras.map((op) => (
                <option key={op.id} value={op.id}>{op.nome_display || op.nome}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Status de Pagamento:</label>
            <select
              value={filtroStatus}
              onChange={(e) => handleFiltroStatusChange(e.target.value)}
              className="filter-select"
            >
              <option value="">Todos</option>
              <option value="a_pagar">A Pagar</option>
              <option value="pago">Pago</option>
              <option value="estornado">Estornado</option>
            </select>
          </div>
        </div>

        {/* Fluxo de pagamento por operadora */}
        <section className="fluxo-operadoras-section">
          <h2 className="fluxo-section-title">Fluxo de pagamento por operadora</h2>
          <div className="fluxo-operadoras-grid">
            {operadoras.map((op) => (
              <div key={op.id} className="fluxo-operadora-card">
                <div className="fluxo-operadora-nome">{op.nome_display || op.nome}</div>
                <div className="fluxo-form">
                  <div className="form-group">
                    <label>Tipo de fluxo</label>
                    <select
                      value={op.fluxo_tipo || ''}
                      onChange={(e) => handleFluxoChange(op.id, 'fluxo_tipo', e.target.value || null)}
                      className="form-control"
                    >
                      <option value="">Sem fluxo</option>
                      <option value="semanal">Semanal</option>
                      <option value="mensal">Mensal</option>
                    </select>
                  </div>
                  {op.fluxo_tipo === 'semanal' && (
                    <div className="form-group">
                      <label>Dia da semana</label>
                      <select
                        value={op.fluxo_dia_semana != null ? op.fluxo_dia_semana : 1}
                        onChange={(e) => handleFluxoChange(op.id, 'fluxo_dia_semana', e.target.value)}
                        className="form-control"
                      >
                        {DIAS_SEMANA.map((d) => (
                          <option key={d.value} value={d.value}>{d.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {op.fluxo_tipo === 'mensal' && (
                    <div className="form-group">
                      <label>Dia do mês</label>
                      <select
                        value={op.fluxo_dia_mes != null ? op.fluxo_dia_mes : 1}
                        onChange={(e) => handleFluxoChange(op.id, 'fluxo_dia_mes', e.target.value)}
                        className="form-control"
                      >
                        {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="fluxo-card-actions">
                    <button
                      type="button"
                      className="btn-primary btn-sm"
                      onClick={() => handleSaveFluxo(op)}
                      disabled={savingFluxoId === op.id}
                    >
                      {savingFluxoId === op.id ? 'Salvando...' : 'Salvar'}
                    </button>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => openResumoModal(op)}
                      disabled={loadingResumo}
                    >
                      Ver valor a receber
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {operadoras.length === 0 && !loading && (
            <p className="fluxo-empty">Nenhuma operadora cadastrada.</p>
          )}
        </section>

        {loading ? (
          <div className="loading">Carregando vendas...</div>
        ) : vendas.length === 0 ? (
          <div className="empty-state">
            <p>📭 Nenhuma venda concluída encontrada</p>
          </div>
        ) : (
          <>
          <div className="table-container">
            <table className="vendas-pagamento-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Cliente</th>
                  <th>Documento</th>
                  <th>Vendedor</th>
                  <th>Valor Total</th>
                  <th>Status Pagamento</th>
                  <th>Data Pagamento</th>
                  <th>Data Venda</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {vendas.map((venda) => (
                  <tr key={venda.id}>
                    <td>#{venda.id}</td>
                    <td>{venda.nome_cliente || venda.razao_social}</td>
                    <td>{formatDocumento(venda.documento, venda.tipo_cliente)}</td>
                    <td>{venda.vendedor?.nome || 'N/A'}</td>
                    <td>R$ {parseFloat(venda.valor_total || 0).toFixed(2).replace('.', ',')}</td>
                    <td>
                      <span className={`status-badge ${getStatusPagamentoClass(venda.status_pagamento)}`}>
                        {getStatusPagamentoText(venda.status_pagamento)}
                      </span>
                    </td>
                    <td>{venda.data_pagamento ? formatDate(venda.data_pagamento) : '-'}</td>
                    <td>{formatDate(venda.created_at)}</td>
                    <td>
                      <button 
                        onClick={() => openPagamento(venda)}
                        className="btn-action"
                      >
                        ✏️ Editar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {totalPages > 1 && (
              <div className="pagination">
                <span className="pagination-info">
                  Mostrando {(page - 1) * LIMITE_POR_PAGINA + 1}–{Math.min(page * LIMITE_POR_PAGINA, total)} de {total} vendas
                </span>
                <div className="pagination-controls">
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >
                    ← Anterior
                  </button>
                  <span className="pagination-page">
                    Página {page} de {totalPages}
                  </span>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    Próxima →
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Modal de edição de pagamento */}
        {selectedVenda && (
          <div className="modal-overlay" onClick={closePagamento}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Editar Status de Pagamento</h2>
                <button className="modal-close" onClick={closePagamento}>✕</button>
              </div>
              
              <div className="modal-body">
                <div className="form-group">
                  <label>Venda #{selectedVenda.id}</label>
                  <div className="venda-info">
                    <p><strong>Cliente:</strong> {selectedVenda.nome_cliente || selectedVenda.razao_social}</p>
                    <p><strong>Documento:</strong> {formatDocumento(selectedVenda.documento)}</p>
                    <p><strong>Valor Total:</strong> R$ {parseFloat(selectedVenda.valor_total || 0).toFixed(2).replace('.', ',')}</p>
                    <p><strong>Vendedor:</strong> {selectedVenda.vendedor?.nome || 'N/A'}</p>
                  </div>
                </div>

                <form onSubmit={handleUpdatePagamento}>
                  <div className="form-group">
                    <label htmlFor="status_pagamento">Status de Pagamento *</label>
                    <select
                      id="status_pagamento"
                      name="status_pagamento"
                      value={pagamentoData.status_pagamento}
                      onChange={handlePagamentoChange}
                      required
                      className="form-control"
                    >
                      <option value="a_pagar">A Pagar</option>
                      <option value="pago">Pago</option>
                      <option value="estornado">Estornado</option>
                    </select>
                  </div>

                  {pagamentoData.status_pagamento === 'pago' && (
                    <div className="form-group">
                      <label htmlFor="data_pagamento">Data do Pagamento *</label>
                      <input
                        type="date"
                        id="data_pagamento"
                        name="data_pagamento"
                        value={pagamentoData.data_pagamento || new Date().toISOString().split('T')[0]}
                        onChange={handlePagamentoChange}
                        required
                        className="form-control"
                      />
                    </div>
                  )}

                  <div className="modal-actions">
                    <button type="button" onClick={closePagamento} className="btn-secondary">
                      Cancelar
                    </button>
                    <button type="submit" className="btn-primary" disabled={updating}>
                      {updating ? 'Salvando...' : 'Salvar'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Quanto o vendedor tem a receber */}
        {(operadoraResumo || loadingResumo) && (
          <div className="modal-overlay" onClick={loadingResumo ? undefined : closeResumoModal}>
            <div className="modal-content modal-resumo" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Valor a receber por vendedor</h2>
                <button type="button" className="modal-close" onClick={closeResumoModal} disabled={loadingResumo}>✕</button>
              </div>
              <div className="modal-body">
                {loadingResumo ? (
                  <div className="loading">Carregando resumo...</div>
                ) : operadoraResumo ? (
                  <>
                    <p className="resumo-operadora-nome"><strong>Operadora:</strong> {operadoraResumo.operadora_nome}</p>
                    {operadoraResumo.resumo && operadoraResumo.resumo.length > 0 ? (
                      <>
                        <table className="resumo-table">
                          <thead>
                            <tr>
                              <th>Vendedor</th>
                              <th>Valor a receber</th>
                              <th>PIX</th>
                            </tr>
                          </thead>
                          <tbody>
                            {operadoraResumo.resumo.map((r) => (
                              <tr key={r.vendedor_id}>
                                <td>{r.vendedor_nome}</td>
                                <td>R$ {Number(r.total_a_receber).toFixed(2).replace('.', ',')}</td>
                                <td className="resumo-pix">{r.vendedor_pix || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td><strong>Total</strong></td>
                              <td><strong>R$ {Number(operadoraResumo.total_geral || 0).toFixed(2).replace('.', ',')}</strong></td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </table>
                      </>
                    ) : (
                      <p className="resumo-empty">Nenhuma venda a pagar para esta operadora.</p>
                    )}
                  </>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default VendasPagamento;
